import React, { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  CheckCircle2,
  TriangleAlert,
  BarChart3,
  ShieldCheck,
  Eye,
  EyeOff,
  // CircleHelp,
  // Globe,
  // ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import styles from "./Login.module.css";
import Logo from "../../Assets/PentLogo.png";
import Background from "../../Assets/Background.png";

import AccessLogo from "../../Assets/ogsg.png";
import UbaLogo from "../../Assets/funaab.png";
import GTLogo from "../../Assets/rccg.png";
import FidelityLogo from "../../Assets/gate.png";
import { BASE_URL } from '../Api/Api';
import { Button, Spinner } from "react-bootstrap";

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError("");

    if (!email || !password) {
      setError("Please enter both email and password.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${BASE_URL}/login`, {
        email,
        password,
      });

      console.log("Full response:", response.data);

      const token =
        response.data?.token ||
        response.data?.access_token ||
        response.data?.data?.token ||
        response.data?.data?.access_token;

      if (!token) {
        console.warn("No token found in response", response.data);
        Swal.fire({
          icon: "error",
          title: "Login Incomplete",
          text: "Authentication token missing. Please contact support.",
          confirmButtonColor: "rgb(222 36 54)",
        });
        setLoading(false);
        return;
      }

      const customer =
        response.data?.data?.customer || response.data?.customer || {};
      const loggedInUser = response.data?.data?.user || response.data?.user || {};
      const companies = response.data?.data?.companies || response.data?.companies || [];
      const activeCompany =
        companies.find((company) => company.membership_status === "active") ||
        companies[0] ||
        {};

      const firstName = customer.first_name || loggedInUser.name?.split(" ")[0] || "";
      const lastName = customer.last_name || loggedInUser.name?.split(" ").slice(1).join(" ") || "";
      const phone = customer.phone_number || "";
      const emailFromApi = customer.email || loggedInUser.email || "";
      const userType = customer.type || "";
      const customerId =
        customer.id ||
        customer.customer_id ||
        customer.customerId ||
        customer.customerID ||
        "";
      const userId =
        loggedInUser.id ||
        customer.user_id ||
        customer.userId ||
        customer.id ||
        customer.customer_id ||
        customer.customerId ||
        "";
      const companyId =
        activeCompany.id ||
        customer.company_id ||
        customer.companyId ||
        customer.company?.id ||
        response.data?.data?.company_id ||
        response.data?.company_id ||
        "";

      localStorage.setItem("userToken", token);
      localStorage.setItem("firstName", firstName);
      localStorage.setItem("lastName", lastName);
      localStorage.setItem("phone", phone);
      localStorage.setItem("email", emailFromApi);
      localStorage.setItem("userType", userType);
      if (customerId) {
        localStorage.setItem("customerId", String(customerId));
      }
      if (userId) {
        localStorage.setItem("userId", String(userId));
      }
      if (companyId) {
        localStorage.setItem("companyId", String(companyId));
      }

      console.log("Saved auth token and user details");

      navigate("/dashboard");
    } catch (err) {
      let errorMessage = "An unexpected error occurred. Please try again.";

      if (err.response && err.response.data) {
        const msg = err.response.data.message;
        if (typeof msg === "string") {
          errorMessage = msg;
        } else if (Array.isArray(msg)) {
          errorMessage = msg.join("; ");
        } else if (msg && typeof msg === "object") {
          errorMessage = JSON.stringify(msg);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: errorMessage,
        confirmButtonColor: "rgb(222 36 54)",
        confirmButtonText: "Try Again",
      });

      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  const isButtonDisabled = !email || !password || loading;

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isButtonDisabled) {
      handleSubmit(e);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.loginContainer}>
        {/* =========================
            LEFT SIDE
        ========================= */}
        <section className={styles.heroSection}>
          <img
            src={Background}
            alt=""
            className={styles.backgroundImage}
          />
          <div className={styles.heroOverlay}></div>

          <div className={styles.heroContent}>
            <div className={styles.logoContainer}>
              <img
                src={Logo}
                alt="PENTSUITE"
                className={styles.logoImage}
              />
              <div className={styles.logoNameCont}>
                <h1>PENTSUITE</h1>
                <p>RECONCILIATION & CONTROL SYSTEM</p>
              </div>
            </div>

            <div className={styles.heroHeading}>
              <h1>
                Reconcile <span>Smarter</span>
                <br />
                Control with <span>Confidence</span>
              </h1>

              <p>
                <span className={styles.pentLogss}> PENTSUITE </span> helps
                finance teams automate reconciliation,
                <br />
                resolve exceptions and close with confidence
              </p>
            </div>

            <div className={styles.features}>
              <div className={styles.feature}>
                <div className={styles.featureIcon}>
                  <CheckCircle2 size={18} strokeWidth={2} />
                </div>
                <div className={styles.featureText}>
                  <h3>Automated Reconciliation</h3>
                  <p>
                    Match transactions accurately with intelligent automation
                  </p>
                </div>
              </div>

              <div className={styles.feature}>
                <div className={styles.featureIcon}>
                  <TriangleAlert size={18} strokeWidth={2} />
                </div>
                <div className={styles.featureText}>
                  <h3>Exception Management</h3>
                  <p>Identify and resolve exceptions faster than ever</p>
                </div>
              </div>

              <div className={styles.feature}>
                <div className={styles.featureIcon}>
                  <BarChart3 size={18} strokeWidth={2} />
                </div>
                <div className={styles.featureText}>
                  <h3>Real-time Insights</h3>
                  <p>
                    Get real-time visibility into cash, balances and variances
                  </p>
                </div>
              </div>

              <div className={styles.feature}>
                <div className={styles.featureIcon}>
                  <ShieldCheck size={18} strokeWidth={2} />
                </div>
                <div className={styles.featureText}>
                  <h3>Audit Ready</h3>
                  <p>
                    Complete audit trail and controls for peace of mind
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.trustedSection}>
              <p className={styles.trustedText}>
                Trusted by forward thinking organizations
              </p>

              <div className={styles.trustedLogos}>
                <img
                  src={AccessLogo}
                  alt="Access Bank"
                  className={styles.trustedLogo}
                />
                <img
                  src={UbaLogo}
                  alt="UBA"
                  className={styles.trustedLogo}
                />
                <img
                  src={GTLogo}
                  alt="GTCO"
                  className={styles.trustedLogo}
                />
                <img
                  src={FidelityLogo}
                  alt="Fidelity"
                  className={styles.trustedLogo}
                />
              </div>
            </div>
          </div>
        </section>

        {/* =========================
            RIGHT SIDE
        ========================= */}
        <section className={styles.formSection}>
          <div className={styles.loginArea}>
            <div className={styles.loginCard}>
              <div className={styles.formHeader}>
                <h2>Welcome Back</h2>
                <p>
                  Sign in to your{" "}
                  <span className={styles.pentLogss}>PENTSUITE</span> account
                </p>
              </div>

              {/* LOGIN FORM */}
              <div>
                {error && (
                  <div className={styles.errorMessage} style={{ color: "red", marginBottom: "1rem" }}>
                    {error}
                  </div>
                )}

                {/* EMAIL */}
                <div className={styles.inputGroup}>
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    placeholder="enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                {/* PASSWORD */}
                <div className={styles.inputGroup}>
                  <label htmlFor="password">Password</label>

                  <div className={styles.passwordWrapper}>
                    <input
  id="password"
  type={showPassword ? "text" : "password"}
  placeholder="enter your password"
  value={password}
  onChange={(e) => setPassword(e.target.value)}
  required
  onKeyDown={handleKeyPress}
/>

                    <button
                      type="button"
                      className={styles.eyeButton}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>

                  <div className={styles.forgotPassword}>
                    <button type="button">Forgot Password?</button>
                  </div>
                </div>

                {/* Sign In Button */}
                <Button
                variant="success"
                  onClick={(e) => handleSubmit(e)}
                  className={styles.signInButton}
                  disabled={isButtonDisabled}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                   {loading ? (
                            <>
                                <Spinner size='sm' />
                                <span style={{ marginLeft: '5px' }}>Signing in...</span>
                            </>
                        ) : (
                            "Sign In"
                        )}  
                </Button>
              </div>

              {/* OR */}
              <div className={styles.orDivider}>
                <span></span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 3,
                  }}
                >
                  <p>or you do not have an account yet?</p>
                  <div>Register here!</div>
                </div>
                <span></span>
              </div>

              {/* SECURITY */}
              <div className={styles.securitySection}>
                <div className={styles.securityIcon}>
                  <ShieldCheck size={25} strokeWidth={1.8} />
                </div>

                <div className={styles.securityText}>
                  <strong>
                    Your data is protected with enterprise-grade security
                  </strong>

                  <div className={styles.securityDetails}>
                    <span>ISO 27001 Certified</span>
                    <i></i>
                    <span>Bank-level Encryption</span>
                    <i></i>
                    <span>Secure &amp; Compliant</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Login;